import { ModalViewModel } from '@/infra/ModalViewModel'
import { AsyncPrompt } from '@/infra/AsyncPrompt'
import { action, observable } from 'mobx'
import { task } from 'mobx-task'
import { ignoreAbortError, isAbortError } from '@/utils/abort-util'
import type { SnackbarErrorHandlerFactory } from '@/features/shell'
import { Section7216 } from '@/features/account/section-7216/Section7216'
import type { Section7216Store } from '@/features/account/section-7216/Section7216Store'
import type { EnsureSection7216AcceptanceResult } from '@/features/account'
import { validationContext, required } from 'validx'
import { mustHaveMaxLength, when } from '@/utils/validx-util'

export interface Section7216AcceptancePromptOptions {
  isSpouseSignatureRequired: boolean
  signal?: AbortSignal
}

export class Section7216AcceptanceDialogViewModel {
  readonly modal = new ModalViewModel()

  private readonly _prompt = new AsyncPrompt<boolean>({
    abort$: this.modal.close$,
  })

  @observable accessor isSpouseSignatureRequired = false
  @observable accessor section7216: Section7216 = Section7216.none
  @observable accessor userSignature = ''
  @observable accessor spouseSignature = ''

  readonly validation = validationContext(this, {
    userSignature: [
      required('User signature is required'),
      mustHaveMaxLength(500, 'Signature must not exceed 500 characters'),
    ],
    spouseSignature: [
      when(
        () => this.isSpouseSignatureRequired,
        required('Spouse signature is required'),
      ),
      when(
        () => this.isSpouseSignatureRequired,
        mustHaveMaxLength(
          500,
          'Spouse signature must not exceed 500 characters',
        ),
      ),
    ],
  })

  constructor(
    private readonly _createSnackbarErrorHandler: SnackbarErrorHandlerFactory,
    private readonly _store: Section7216Store,
  ) {}

  async prompt(
    options: Section7216AcceptancePromptOptions,
  ): Promise<EnsureSection7216AcceptanceResult> {
    const { isSpouseSignatureRequired, signal } = options

    this.setIsSpouseSignatureRequired(isSpouseSignatureRequired)

    const acceptance = await this._store.fetchLatestAcceptance(signal)
    // Only return 'Accepted' if acceptance exists and, if spouseRequired, spouseSignature is present or spouse is not required.
    if (
      acceptance &&
      (!isSpouseSignatureRequired || !!acceptance.spouseSignature)
    ) {
      return 'Accepted'
    }

    if (signal?.aborted) {
      return 'Aborted'
    }

    const promise = this._prompt.prompt(signal)
    this.modal.show()
    try {
      const fetchLatestResult = await this.fetchLatest(signal)
      if (isAbortError(fetchLatestResult)) {
        return 'Aborted'
      }

      const response = await promise
      if (signal?.aborted) {
        return 'Aborted'
      }

      return response ? 'Accepted' : 'Declined'
    } finally {
      this.modal.close()
    }
  }

  fetchLatest = task(async (signal?: AbortSignal) => {
    const section7216 = await this._store
      .fetchLatest(signal)
      .catch(ignoreAbortError())
      .catch(this._createSnackbarErrorHandler('Fetching Section 7216 failed'))

    if (isAbortError(section7216)) {
      return section7216
    }

    this.setSection7216(section7216)
    return section7216
  })

  @action.bound
  setIsSpouseSignatureRequired(isSpouseSignatureRequired: boolean) {
    this.isSpouseSignatureRequired = isSpouseSignatureRequired
  }

  @action.bound
  setSection7216(section7216: Section7216) {
    this.section7216 = section7216
  }

  @action.bound
  setUserSignature(v: string) {
    this.userSignature = v
  }

  @action.bound
  setSpouseSignature(v: string) {
    this.spouseSignature = v
  }

  accept = task.resolved(async () => {
    if (!this.validation.reset().validate().isValid) {
      return
    }

    const spouseSignature = this.isSpouseSignatureRequired
      ? this.spouseSignature
      : undefined
    await this._store
      .acceptLatest({
        userSignature: this.userSignature,
        spouseSignature: spouseSignature,
      })
      .then(() => {
        this.setUserSignature('')
        this.setSpouseSignature('')
      })
      .catch(this._createSnackbarErrorHandler('Accepting Section 7216 failed'))
    this._prompt.respond(true)
  })

  /**
   * Declines the section 7216.
   */
  @action.bound
  decline() {
    this._prompt.respond(false)
  }
}
